/**
 * Version-bound baked-asset SYNC (upload → persist refs) — the shared network core behind BOTH
 * TemplateEditor's onBaked upload AND the Wizard's post-submit upload, so a template baked in
 * either surface publishes the same layered assets every device can resolve.
 *
 * It lives in a module SEPARATE from `bakedSync.ts` (which owns the low-level `uploadBakedAssets`)
 * on purpose: calling `uploadBakedAssets` across a module boundary keeps it interceptable by a
 * test `vi.mock("./bakedSync.js", …)` — a same-module local call never is — so the editor's
 * banner/retry/persist tests keep exercising the REAL wrapper.
 */
import type { FitMode, MockupAssets, ShadingParams, VarnishParams } from "@genz/engine-bind";
import type { AppLogger } from "../app/context.js";
import { uploadBakedAssets, type BakedAssetRefs } from "./bakedSync.js";
import { fetchWithTimeout, UPLOAD_TIMEOUT_MS } from "./timeout.js";

/**
 * WS-P / AUD-8 LB-8 (i): hosting is off for this workspace, so a cross-device bake cannot be
 * published. Thrown BEFORE the first presign, so the operator learns it before the pixels are
 * encoded rather than as a 409 afterwards — or, as happened in the field, not at all.
 *
 * `code` is byte-identical to the cloud's own 409 code, so one handler covers both the
 * pre-flight and the backstop.
 */
export class HostingDisabledError extends Error {
  readonly code = "hosting_not_enabled";

  constructor() {
    super(
      "Cross-device mock-ups are off for this workspace, so the baked mock-up cannot be " +
      "published and other devices will render this template design-only.",
    );
    this.name = "HostingDisabledError";
  }
}

/**
 * The workspace's `hosting_enabled`, warmed ONCE per session from
 * `GET /api/customization/capabilities` (App.tsx, beside `setModelsBase` — the same house
 * pattern). Read synchronously on the bake path: fetching it there would put a network call
 * inside the very flow this wave exists to make reliable, i.e. a brand-new failure mode.
 *
 * `undefined` means NOBODY SAID — an older cloud, or the warm-up has not landed — and is
 * deliberately NOT `false`. Only an explicit `false` refuses.
 */
let hostingEnabledValue: boolean | undefined;

/** Record what the capabilities payload said. `undefined` resets to "unknown" (sign-out). */
export function setHostingEnabled(enabled: boolean | undefined): void {
  hostingEnabledValue = enabled;
}

/** What the cloud last said about asset hosting; `undefined` when it has not said. */
export function hostingEnabled(): boolean | undefined {
  return hostingEnabledValue;
}

/** The exact payload MaskEditor hands to `onBaked` — the bakeable pixels + geometry + layer
 *  refs a caller uploads for cross-device sync. Shared so BOTH TemplateEditor's onBaked path
 *  AND the Wizard's post-submit path retain and forward the SAME shape. */
export interface BakedUploadPayload {
  assets: MockupAssets;
  /** Product mask as a grayscale PNG (255 inside the product). */
  maskPng: Uint8Array;
  /** Print-area mask as a grayscale PNG (255 inside the print region), when present. */
  printMaskPng?: Uint8Array;
  /** Raw AI-matte as a grayscale PNG (255 = kept object), when a removal produced one. */
  mattePng?: Uint8Array;
  aiEdge?: boolean;
  aiEdgeKeep?: number;
  /** Per-template edge-calibration sliders, forwarded so reopening restores the calibrated
   *  cutout. 0/negative are meaningful and preserved end-to-end. */
  edgeFeather?: number;
  matteKind?: "ai" | "chroma";
  edgeChoke?: number;
  matteStrength?: number;
  /** The version's EXISTING baked-asset refs (last loaded/saved). Threaded so a matte-less re-bake
   *  CARRIES FORWARD an already-hosted matte + AI-edge state instead of the cloud full-overwrite
   *  wiping it (fixes "AI-remove button resets on reopen while the preview shows the processed image"). */
  previousRefs?: BakedAssetRefs | null;
  /** The user deliberately cleared the removal this session ("Restore background") — suppresses
   *  carry-forward so the matte fields are intentionally dropped, not resurrected. */
  matteCleared?: boolean;
  layerRefs?: {
    textureUrl?: string;
    clearBgUrl?: string;
    /** Whether the "Texture / effect layer" was on for this bake — persisted separately from
     *  `textureUrl` because a UV-varnish layer has no texture image (see BakedAssetRefs.texture_on). */
    textureOn?: boolean;
    textureBlend?: string;
    varnish?: VarnishParams;
    designRotation?: number;
    fillFit?: FitMode;
    designAspect?: [number, number] | null;
    printShadingStrength?: number;
    shading?: ShadingParams;
    stageDespill?: boolean;
    stageRefine?: boolean;
    stageMatteStrength?: boolean;
    stageShading?: boolean;
    matteQuality?: "fast" | "quality";
    /** Marker keying colour/tolerance, "Protect product", and the product-profile category —
     *  all reshape the baked shell/print mask, so they persist alongside the pixels (audit
     *  P1-1/P1-2/P1-3). Without them a reopen re-keyed with the defaults and the next Save
     *  uploaded a WRONG shell + print mask over the good ones. */
    markerColor?: [number, number, number] | null;
    markerTol?: number;
    shadedMagentaGuide?: boolean;
    protectProduct?: boolean;
    productCategory?: string;
  };
}

/** The clear-case layer bytes when the layer is on and nothing hosts them yet. */
function unhostedClearBg(assets: MockupAssets, clearBgUrl?: string): Uint8Array | undefined {
  return assets.clearPhone && assets.phone && !clearBgUrl ? assets.phone : undefined;
}

/** The minimal client surface `syncBakedAssetsToCloud` needs (presign + persist). Structural so
 *  it's trivially satisfied by JsonClient and mocked in tests. */
export interface BakedSyncClient {
  templateReferenceUpload(
    templateId: string,
    ext: string,
  ): Promise<{ upload_url: string; public_url: string; fields?: Record<string, string> }>;
  setBakedAssets(
    templateId: string,
    versionNumber: number,
    bakedAssets: Record<string, unknown>,
  ): Promise<{ versionNumber: number; versionToken: string }>;
}

/** Host one baked PNG for a template and return its public URL. Re-key all uses it, so this file
 *  stays the one place that PUTs baked assets (see docs/r2-cors.md). */
export async function hostBakedPng(
  client: Pick<BakedSyncClient, "templateReferenceUpload">, templateId: string, bytes: Uint8Array,
): Promise<string> {
  const target = await client.templateReferenceUpload(templateId, "png");
  const res = await fetchWithTimeout(target.upload_url, {
    method: "PUT", headers: { ...(target.fields ?? {}) }, body: bytes as BodyInit,
  }, UPLOAD_TIMEOUT_MS, "Baked asset upload");
  if (!res.ok) throw new Error(`the upload was refused (${res.status})`);
  return target.public_url;
}

/**
 * VERSION-BOUND baked-asset sync: presign + PUT the baked pixels (via `uploadBakedAssets`, which
 * HEAD-verifies + retries a fresh presign), then persist the resulting refs on the version with
 * `setBakedAssets`. React state (banners / retry refs) is the CALLER's job; this function only does
 * the network work and returns the rotated version token.
 *
 * `setBakedAssets` bumps the version_token server-side (optimistic-concurrency), so the returned
 * `newVersionToken` must be adopted by any caller that keeps editing the same version (the editor
 * does; the wizard is done with the version, so it can ignore it).
 *
 * Throws on any upload/persist failure (a file:// target, an unreachable host, a rejected PUT, an
 * object that stays unfetchable, or a failed persist) — the caller decides how to surface it. It
 * NEVER swallows: a silent failure is exactly why "the mockup doesn't show on another device" was
 * undiagnosable.
 */
export async function syncBakedAssetsToCloud(
  client: BakedSyncClient,
  logger: AppLogger | undefined,
  opts: {
    templateId: string;
    versionNumber: number;
    payload: BakedUploadPayload;
    /** Which surface triggered the sync ("wizard" | "editor") — carried in the breadcrumb
     *  detail so a wizard-created template's asset upload is distinguishable from an editor
     *  re-bake in the activity trail. Optional; omitted detail keys are simply absent. */
    surface?: "wizard" | "editor";
    /** Multi-view (Task B4): map the freshly-uploaded per-view `refs` into the FULL
     *  `baked_assets` dict to persist — e.g. `mergeViewRefs(lastKnownRefs, activeView, refs)`
     *  so a per-view bake writes its own view without wiping sibling views. Omitted ⇒ the flat
     *  refs are persisted as-is (the single-view / wizard path, behaviourally unchanged). */
    mapRefs?: (refs: BakedAssetRefs) => Record<string, unknown>;
    /** WS-P: the workspace's `hosting_enabled`, as the cloud last reported it. `false` refuses
     *  BEFORE the first presign. `undefined` (an older cloud, or the capability not yet loaded)
     *  does NOT refuse — the presign's own 409 is still the backstop, and pre-empting it on a
     *  value nobody sent would stop baking outright for those clients. */
    hostingEnabled?: boolean | undefined;
  },
): Promise<{ newVersionToken: string; versionNumber: number }> {
  const { templateId, versionNumber, payload, surface, mapRefs } = opts;
  // PRE-FLIGHT, before a single byte is encoded or a single request made. The 409 this
  // anticipates used to arrive after the whole bake had been uploaded — and only reached a
  // human as "the cloud copy didn't sync", which named neither the cause nor the fix.
  if (opts.hostingEnabled === false) {
    logger?.log("error", "Sync: baked-asset cross-device sync FAILED (other devices render design-only)", {
      error: "hosting_not_enabled", step: "hostingPreflight", templateId, versionNumber,
    });
    throw new HostingDisabledError();
  }
  const { assets, maskPng, printMaskPng, mattePng, aiEdge, aiEdgeKeep, edgeFeather, edgeChoke, matteStrength, matteKind, previousRefs, matteCleared, layerRefs } = payload;
  const refs = await uploadBakedAssets(
    {
      generated: assets.generated, shell: assets.shell, maskPng, printMaskPng,
      printQuad: assets.printQuad, clearPhone: assets.clearPhone, opacity: assets.opacity,
      // Persist the RAW matte + AI-edge state so reopening restores the "background removed"
      // state AND the calibration slider (which reads the raw matte).
      mattePng, aiEdge, aiEdgeKeep,
      // Persist the per-template edge-calibration sliders so reopening restores the exact cutout.
      edgeFeather, edgeChoke, matteStrength, matteKind,
      // Carry the version's existing matte forward on a matte-less re-bake (unless deliberately
      // cleared) so the cloud full-overwrite doesn't wipe the removal state on reopen.
      previousRefs, matteCleared,
      // Persist the picked LAYER library URLs + placement/fill so the texture / clear-case
      // background + rotation/fill restore when the template opens on another system.
      textureUrl: layerRefs?.textureUrl, clearBgUrl: layerRefs?.clearBgUrl,
      clearBg: unhostedClearBg(assets, layerRefs?.clearBgUrl),
      textureOn: layerRefs?.textureOn,
      textureBlend: layerRefs?.textureBlend, varnish: layerRefs?.varnish,
      designRotation: layerRefs?.designRotation,
      fillFit: layerRefs?.fillFit, designAspect: layerRefs?.designAspect,
      // Persist the RAW artwork-shading strength + pipeline-stage toggles + matte quality so
      // reopening restores the exact control values (the effective shading rides `assets`).
      printShadingStrength: layerRefs?.printShadingStrength, shading: layerRefs?.shading, stageDespill: layerRefs?.stageDespill,
      stageRefine: layerRefs?.stageRefine, stageMatteStrength: layerRefs?.stageMatteStrength,
      stageShading: layerRefs?.stageShading, matteQuality: layerRefs?.matteQuality,
      // Marker keying + protect-product + product category (see BakedUploadPayload.layerRefs).
      markerColor: layerRefs?.markerColor, markerTol: layerRefs?.markerTol,
      shadedMagentaGuide: layerRefs?.shadedMagentaGuide,
      protectProduct: layerRefs?.protectProduct, productCategory: layerRefs?.productCategory,
    },
    {
      presign: () => client.templateReferenceUpload(templateId, "png"),
      putBytes: async (url, bytes, fields) => {
        if (url.startsWith("file://")) {
          // Local hosting can't host baked assets for OTHER devices — a file:// target isn't
          // fetchable off-box. Silently no-op'ing here USED to persist refs pointing at objects
          // nobody else can resolve → synced devices 404 → design-only render. Fail loudly.
          logger?.log("error", "Sync: local hosting cannot host baked assets — cross-device sync skipped", {
            step: "putBytes", host: "file://", bytes: bytes.byteLength,
          });
          throw new Error("local hosting cannot host baked assets — cloud sync skipped");
        }
        let host = url;
        try { host = new URL(url).host; } catch { /* keep raw url on parse failure */ }
        let res: Response;
        try {
          res = await fetchWithTimeout(url, { method: "PUT", headers: { ...(fields ?? {}) }, body: bytes as BodyInit },
            UPLOAD_TIMEOUT_MS, "Baked asset upload");
        } catch (netErr) {
          // The fetch itself failed (network/CORS/WebView) — the single most useful signal for
          // the "saves don't sync" reports. Log it WITH detail.
          logger?.log("error", "Sync: baked-asset PUT could not reach hosting (fetch failed)", {
            error: netErr, step: "putBytes", host, bytes: bytes.byteLength,
          });
          throw netErr;
        }
        if (!res.ok) {
          logger?.log("error", "Sync: baked-asset PUT rejected by hosting", {
            step: "putBytes", host, status: res.status, bytes: bytes.byteLength,
          });
          throw new Error(`baked upload PUT ${host} -> ${res.status}`);
        }
        logger?.breadcrumb("action", "Sync: baked-asset uploaded", {
          host, status: res.status, bytes: bytes.byteLength,
        });
      },
    },
    // Pass the logger so uploadBakedAssets' per-object verify ladder emits the same structured
    // miss/retry/unverifiable trail into the activity/error log.
    logger,
  );
  logger?.breadcrumb("action", "Sync: assets uploaded — persisting refs to cloud", {
    templateId, versionNumber, ...(surface ? { surface } : {}),
  });
  // Persist the FULL baked-assets dict. `mapRefs` (editor multi-view) merges this view's fresh
  // refs into the existing per-view map so sibling views survive; without it the flat refs persist
  // as-is (single-view / wizard path).
  const toPersist = mapRefs ? mapRefs(refs) : (refs as unknown as Record<string, unknown>);
  const updated = await client.setBakedAssets(
    templateId, versionNumber, toPersist,
  );
  logger?.breadcrumb("action", "Sync: baked refs persisted — other devices will render the full mockup", {
    templateId, versionNumber: updated.versionNumber, ...(surface ? { surface } : {}),
  });
  return { newVersionToken: updated.versionToken, versionNumber: updated.versionNumber };
}
